import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Upload, X, Loader2 } from "lucide-react";

const DISPUTE_TYPES = [
  { id: "damaged", label: "Marchandise abîmée" },
  { id: "missing", label: "Marchandise manquante" },
  { id: "late", label: "Retard important" },
  { id: "payment", label: "Différend de paiement" },
  { id: "other", label: "Autre" },
];

function readAsDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function DisputeDialog({ open, mission, mode = "open", dispute, onClose, onDone }) {
  // mode: "open" (créer un litige) | "respond" (répondre au litige existant)
  const [type, setType] = useState("damaged");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]); // array of dataURL
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setType("damaged"); setDescription(""); setPhotos([]); setAmount(""); }
  }, [open]);

  const addPhotos = async (files) => {
    const arr = Array.from(files || []).slice(0, 5 - photos.length);
    if (arr.length === 0) return;
    const datas = await Promise.all(arr.map(readAsDataURL));
    setPhotos((p) => [...p, ...datas].slice(0, 5));
  };

  const submit = async () => {
    if (description.trim().length < 10) {
      toast.error("Description trop courte (10 caractères minimum)");
      return;
    }
    setBusy(true);
    try {
      if (mode === "open") {
        await api.post(`/missions/${mission.id}/dispute`, {
          type,
          description: description.trim(),
          photos,
          amount_contested: amount ? parseFloat(amount) : null,
        });
        toast.success("Litige ouvert — l'autre partie a 7 j pour répondre");
      } else {
        await api.post(`/disputes/${dispute.id}/respond`, {
          message: description.trim(), photos,
        });
        toast.success("Réponse envoyée — un admin va trancher");
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isOpenMode = mode === "open";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            {isOpenMode ? "Signaler un litige" : "Répondre au litige"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isOpenMode
              ? "Délai de 4 j après livraison. Le paiement sera mis en pause jusqu'à la résolution par notre équipe (max 7 j)."
              : "Donnez votre version des faits et ajoutez des preuves. Un admin tranchera ensuite."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isOpenMode && (
            <div>
              <Label className="overline">Type de problème</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {DISPUTE_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setType(t.id)}
                    className={`py-3 px-3 text-sm border rounded-md text-left transition-colors ${
                      type === t.id
                        ? "bg-[#1F4032] text-white border-[#1F4032]"
                        : "bg-white border-neutral-300 hover:border-neutral-500"
                    }`}
                    data-testid={`dispute-type-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="overline">
              {isOpenMode ? "Description détaillée" : "Votre réponse"}
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder={isOpenMode
                ? "Expliquez le problème en détail (état des marchandises, faits constatés, heure et lieu...)"
                : "Apportez votre version des faits et toute information utile au litige."}
              className="rounded-md mt-1"
              data-testid="dispute-desc"
            />
            <div className="text-xs text-neutral-500 mt-1 text-right">{description.length}/2000</div>
          </div>
          <div>
            <Label className="overline">Photos (jusqu&apos;à 5)</Label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
              {photos.map((p, i) => (
                <div key={`${p.slice(-32)}-${i}`} className="relative aspect-square border border-neutral-200 rounded-md overflow-hidden bg-neutral-50">
                  <img src={p} alt={`document ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((arr) => arr.filter((_, idx) => idx !== i))}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
                    data-testid={`dispute-remove-photo-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="aspect-square border-2 border-dashed border-neutral-300 rounded-md flex items-center justify-center cursor-pointer hover:border-[#1F4032] hover:bg-[#1F4032]/5 transition-colors">
                  <input
                    type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => addPhotos(e.target.files)}
                    data-testid="dispute-photo-input"
                  />
                  <Upload className="w-5 h-5 text-neutral-400" />
                </label>
              )}
            </div>
          </div>
          {isOpenMode && (
            <div>
              <Label className="overline">Montant contesté (€) — facultatif</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Remboursement souhaité"
                className="rounded-md mt-1 h-11"
                data-testid="dispute-amount"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-full">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || description.trim().length < 10}
            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white gap-2"
            data-testid="dispute-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>
              : isOpenMode ? "Ouvrir le litige" : "Envoyer ma réponse"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
